import React, { useCallback, useMemo, useState } from 'react';

import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';

import TMXCircularProgress from 'components/progress/TMXCircularProgress';
import { getTeamRoute } from 'config/router/utils';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import { useStyles } from './styles';

interface TeamsChipsContainerProps {
  tournamentId: string;
  teams: ParticipantInterface[];
  participantId: string;
  playerName: string;
  onDelete?: (playerId: string, playerName: string, teamId: string, teamName: string) => Promise<void> | void;
}

export const TeamsChipsContainer: React.FC<React.PropsWithChildren<TeamsChipsContainerProps>> = React.memo(
  ({ tournamentId, teams, participantId, playerName, onDelete }) => {
    const handleDelete = useCallback(
      (e: any, team: ParticipantInterface) => {
        return onDelete?.(participantId, playerName, team.participantId, team.participantName);
      },
      [onDelete, participantId, playerName],
    );

    return useMemo(
      () => (
        <>
          {teams.map((team, index) => (
            <TeamChip
              key={index}
              href={!!tournamentId && getTeamRoute(tournamentId, team.participantId)}
              label={team.participantName}
              onDelete={(e) => handleDelete(e, team)}
            />
          ))}
        </>
      ),
      [handleDelete, teams, tournamentId],
    );
  },
);

interface TeamChipProps {
  label: string;
  href?: string;
  onDelete?: (event: any) => Promise<void> | void;
}

const TeamChip: React.FC<React.PropsWithChildren<TeamChipProps>> = React.memo(({ label, href, onDelete }) => {
  const { classes } = useStyles();
  const [removing, setRemoving] = useState<boolean>(false);
  return useMemo(
    () => (
      <Chip
        className={classes.teamChip}
        label={href ? <Link to={href}>{label}</Link> : label}
        size="small"
        onDelete={async (e) => {
          setRemoving(true);
          try {
            await onDelete?.(e);
          } finally {
            setRemoving(false);
          }
        }}
        {...(removing && { deleteIcon: <TMXCircularProgress size={12} /> })}
      />
    ),
    [classes.teamChip, href, label, onDelete, removing],
  );
});

export default TeamsChipsContainer;
